import { defineComponent, ref, watch, onBeforeUnmount } from "vue";
import SvgIcon from "../../common/base/SvgIcon.vue.mjs";
import LoadingIcon from "../../common/icons/LoadingIcon.vue.mjs";
import NextIcon from "../../common/icons/NextIcon.vue.mjs";
import TuiButton from "../../common/base/Button.vue.mjs";
import useRoomControl from "./useRoomControlHooks.mjs";
import CreateRoomIcon from "../../common/icons/CreateRoomIcon.vue.mjs";
import EnterRoomIcon from "../../common/icons/EnterRoomIcon.vue.mjs";
import ScheduleRoomIcon from "../../common/icons/ScheduleRoomIcon.vue.mjs";
import useGetRoomEngine from "../../../hooks/useRoomEngine.mjs";
import { useBasicStore } from "../../../stores/basic.mjs";
import { useRoomStore } from "../../../stores/room.mjs";
import AudioMediaControl from "../../common/AudioMediaControl.vue.mjs";
import VideoMediaControl from "../../common/VideoMediaControl.vue.mjs";
import ScheduleConferencePanel from "../../ScheduleConference/ScheduleConferencePanel/index.mjs";
import TUIRoomEngine__default, { TUIRoomDeviceMangerEvents, TRTCVideoMirrorType, TRTCVideoRotation, TRTCVideoFillMode } from "@tencentcloud/tuiroom-engine-js";
import "../../../directives/vClickOutside.mjs";
import { isEnumerateDevicesSupported, isGetUserMediaSupported } from "../../../utils/mediaAbility.mjs";
import useDeviceManager from "../../../hooks/useDeviceManager.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "RoomControlPC",
  props: {
    userName: { default: "" },
    givenRoomId: null,
    enableScheduledConference: { type: Boolean, default: true }
  },
  emits: ["create-room", "enter-room"],
  setup(__props, { expose, emit }) {
    const props = __props;
    const roomStore = useRoomStore();
    const basicStore = useBasicStore();
    const { t } = useRoomControl();
    const { deviceManager, initMediaDeviceList } = useDeviceManager({
      listenForDeviceChange: true
    });
    expose({
      getRoomParam,
      startStreamPreview
    });
    const roomEngine = useGetRoomEngine();
    const isCameraLoading = ref(false);
    const audioVolume = ref(0);
    const isMicMuted = ref(false);
    const isCameraMuted = ref(false);
    const showCreateRoomItems = ref(false);
    const showEnterRoomAction = ref(Boolean(props.givenRoomId));
    const scheduleConferenceDialogVisible = ref(false);
    const scheduleConference = () => {
      scheduleConferenceDialogVisible.value = true;
    };
    const tuiRoomParam = {
      isOpenCamera: true,
      isOpenMicrophone: true,
      defaultCameraId: "",
      defaultMicrophoneId: "",
      defaultSpeakerId: ""
    };
    async function openCamera() {
      var _a, _b;
      const trtcCloud = (_a = roomEngine.instance) == null ? void 0 : _a.getTRTCCloud();
      await (trtcCloud == null ? void 0 : trtcCloud.setLocalRenderParams({
        mirrorType: basicStore.isLocalStreamMirror ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable,
        rotation: TRTCVideoRotation.TRTCVideoRotation0,
        fillMode: TRTCVideoFillMode.TRTCVideoFillMode_Fill
      }));
      await ((_b = roomEngine.instance) == null ? void 0 : _b.startCameraDeviceTest({
        view: "stream-preview"
      }));
    }
    async function closeCamera() {
      var _a;
      await ((_a = roomEngine.instance) == null ? void 0 : _a.stopCameraDeviceTest());
    }
    async function openAudio() {
      var _a;
      await ((_a = roomEngine.instance) == null ? void 0 : _a.startMicDeviceTest({ interval: 200 }));
    }
    async function closeAudio() {
      var _a;
      await ((_a = roomEngine.instance) == null ? void 0 : _a.stopMicDeviceTest());
    }
    async function toggleMuteAudio() {
      isMicMuted.value = !isMicMuted.value;
      tuiRoomParam.isOpenMicrophone = !isMicMuted.value;
      if (isMicMuted.value) {
        await closeAudio();
        audioVolume.value = 0;
      } else {
        await openAudio();
      }
    }
    async function toggleMuteVideo() {
      isCameraMuted.value = !isCameraMuted.value;
      tuiRoomParam.isOpenCamera = !isCameraMuted.value;
      if (isCameraMuted.value) {
        await closeCamera();
        isCameraLoading.value = false;
      } else {
        isCameraLoading.value = true;
        await openCamera();
        isCameraLoading.value = false;
      }
    }
    function getRoomParam() {
      tuiRoomParam.defaultCameraId = roomStore.currentCameraId;
      tuiRoomParam.defaultMicrophoneId = roomStore.currentMicrophoneId;
      tuiRoomParam.defaultSpeakerId = roomStore.currentSpeakerId;
      return tuiRoomParam;
    }
    const onUserVoiceVolume = (options) => {
      audioVolume.value = options.volume;
    };
    async function startStreamPreview() {
      if (!isEnumerateDevicesSupported) {
        return;
      }
      isCameraLoading.value = true;
      await initMediaDeviceList();
      if (!isGetUserMediaSupported) {
        isCameraLoading.value = false;
        return;
      }
      if (roomStore.microphoneList && roomStore.microphoneList.length > 0) {
        openAudio();
      }
      if (roomStore.cameraList && roomStore.cameraList.length > 0) {
        await openCamera();
      }
      isCameraLoading.value = false;
    }
    const roomId = ref(props.givenRoomId);
    watch(
      () => props.givenRoomId,
      (val) => {
        roomId.value = val;
        showEnterRoomAction.value = Boolean(val);
      },
      { immediate: true }
    );
    function handleCreateRoom() {
      showCreateRoomItems.value = !showCreateRoomItems.value;
    }
    function handleClickOutsideCreateRoomItems() {
      if (showCreateRoomItems.value) {
        showCreateRoomItems.value = false;
      }
    }
    function handleEnterRoom() {
      showEnterRoomAction.value = true;
    }
    function createRoom(mode) {
      const roomParam = getRoomParam();
      emit("create-room", {
        roomMode: mode,
        roomParam,
        isSeatEnabled: Boolean(mode === "SpeakAfterTakingSeat")
      });
    }
    function enterRoom() {
      if (!roomId.value) {
        return;
      }
      const roomParam = getRoomParam();
      emit("enter-room", {
        roomId: String(roomId.value),
        roomParam
      });
    }
    TUIRoomEngine__default.once("ready", () => {
      var _a;
      startStreamPreview();
      (_a = deviceManager.instance) == null ? void 0 : _a.on(
        TUIRoomDeviceMangerEvents.onTestMicVolume,
        onUserVoiceVolume
      );
    });
    onBeforeUnmount(async () => {
      var _a;
      await closeAudio();
      await closeCamera();
      (_a = deviceManager.instance) == null ? void 0 : _a.off(
        TUIRoomDeviceMangerEvents.onTestMicVolume,
        onUserVoiceVolume
      );
    });
    return { __sfc: true, roomStore, basicStore, t, deviceManager, initMediaDeviceList, props, roomEngine, isCameraLoading, audioVolume, isMicMuted, isCameraMuted, showCreateRoomItems, showEnterRoomAction, scheduleConferenceDialogVisible, scheduleConference, tuiRoomParam, openCamera, closeCamera, openAudio, closeAudio, toggleMuteAudio, toggleMuteVideo, getRoomParam, onUserVoiceVolume, startStreamPreview, roomId, emit, handleCreateRoom, handleClickOutsideCreateRoomItems, handleEnterRoom, createRoom, enterRoom, SvgIcon, LoadingIcon, NextIcon, TuiButton, CreateRoomIcon, EnterRoomIcon, ScheduleRoomIcon, AudioMediaControl, VideoMediaControl, ScheduleConferencePanel };
  }
});
export {
  _sfc_main as default
};
